import { Controller } from "@hotwired/stimulus"
import debounce from "lodash.debounce"
import { highlight } from "../helpers/text_helper"

export default class AutocompleteController extends Controller {
  static targets = [
    "input",
    "output",
    "suggestionsWrapper",
    "suggestions",
    "emptyValue",
    "create",
    "sessionToken",
  ]

  static outlets = ["autocomplete-creation"]

  static values = {
    choice: { type: Object, default: { label: "" } },
    suggestionsUrl: String,
    supportsMultipleValues: { type: Boolean, default: false },
    shouldShowEmptyValue: { type: Boolean, default: true },
    allowCreate: { type: Boolean, default: false },
    debounceTime: { type: Number, default: 200 },
  }

  initialize() {
    this.suggest = debounce(this.suggest, this.debounceTimeValue).bind(this)
  }

  clearInputValue() {
    this.inputTarget.value = ""
  }

  clearChoice() {
    this.choiceValue = { label: "" }
    this.clearInputValue()
  }

  choose({ params: { value, label, selected } }) {
    if (selected && this.supportsMultipleValuesValue) {
      this.unselectValue(value)
      this.dispatch("chosen", {
        target: this.outputTarget,
        detail: { value: null },
      })
    } else {
      this.outputTarget.value = value
      this.choiceValue = { label: label }
      this.dispatch("chosen", {
        target: this.outputTarget,
        detail: { value: value },
      })
    }

    this.hideSuggestions()
  }

  create(event) {
    this.autocompleteCreationOutlet.create(event)
  }

  unselectValue(value) {
    const form = this.outputTarget.form
    const hiddenInputForValue = form.querySelector(
      `input[type='hidden'][value='${value}']`
    )

    hiddenInputForValue.remove()
  }

  emptyValueTargetConnected() {
    if (this.shouldShowEmptyValueValue) {
      this.emptyValueTarget.classList.remove("hidden")
    }
  }

  createTargetConnected() {
    if (this.allowCreateValue) {
      this.createTarget.classList.remove("hidden")
    }
  }

  async suggest() {
    this.suggestionsWrapperTarget.classList.remove("hidden")

    if (this.hasSuggestionsUrlValue) {
      this.remoteSearch()
    } else {
      this.localSearch()
    }
  }

  hideSuggestions() {
    this.suggestionsWrapperTarget.classList.add("hidden")
    this.inputTarget.value = this.choiceValue.label
  }

  remoteSearch() {
    const url = new URL(this.suggestionsUrlValue)
    url.searchParams.set("query", this.inputTarget.value)

    if (this.hasSessionTokenTarget) {
      url.searchParams.set("session_token", this.sessionTokenTarget.value)
    }

    this.suggestionsWrapperTarget.src = url
  }

  localSearch() {
    const searchQuery = this.inputTarget.value.toLowerCase()

    this.suggestionsTarget.querySelectorAll("li").forEach((listItem) => {
      const listItemLabel = listItem.dataset.autocompleteLabelParam
      const listItemLabelElement = listItem.querySelector(
        "span[data-autocomplete-menu-target='selectableItem']"
      )

      if (listItemLabel.toLowerCase().includes(searchQuery)) {
        listItem.classList.remove("hidden")
        this.highlightMatches(listItemLabelElement, listItemLabel, searchQuery)
      } else {
        listItem.classList.add("hidden")
      }
    })
  }

  highlightMatches(spanElement, originalText, searchQuery) {
    const highlighted = highlight(originalText, searchQuery)
    spanElement.replaceChildren(highlighted)
  }

  closeOnFocusOutside({ target }) {
    if (this.element.contains(target)) return

    this.hideSuggestions()
  }

  isSuggestionsWrapperHidden() {
    return this.suggestionsWrapperTarget.classList.contains("hidden")
  }
}
